/**
 * @fileoverview Google Tag Manager integration hook
 *
 * This hook injects Google Tag Manager scripts into the page,
 *
 * @see https://developers.google.com/tag-manager/quickstart
 */

import { useEffect } from 'react';

// Extend Window interface for GTM dataLayer
declare global {
  interface Window {
    dataLayer: Array<Record<string, unknown>>;
  }
}

/**
 * Hook to initialize Google Tag Manager
 *
 * Injects the GTM script and dataLayer initialization into the page.
 * Should be called once in the root component.
 *
 * @param gtmId - The GTM container ID (e.g., "GTM-XXXXXX")
 *
 * @example
 * ```tsx
 * // In __root.tsx
 * import { useGoogleTagManager } from 'shared/client/hooks/useGoogleTagManager';
 *
 * function RootComponent() {
 *   useGoogleTagManager(import.meta.env.GTM_TAG);
 *   return <Outlet />;
 * }
 * ```
 */
export function useGoogleTagManager(gtmId?: string) {
  useEffect(() => {
    // Skip if no GTM ID provided or if already initialized
    if (!gtmId || document.querySelector(`script[src*="googletagmanager.com/gtm.js?id=${gtmId}"]`)) {
      return;
    }

    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js'
    });

    // Create and inject GTM script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
    const firstScript = document.getElementsByTagName('script')[0];
    firstScript.parentNode?.insertBefore(script, firstScript);

    // Create noscript fallback (GTM requires this for non-JS browsers)
    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);
    document.body.insertBefore(noscript, document.body.firstChild);

    // Cleanup function - typically GTM stays loaded for the session
    // but we clean up for proper React behavior
    return () => {
      // Remove the script
      const gtmScript = document.querySelector(`script[src*="googletagmanager.com/gtm.js?id=${gtmId}"]`);
      if (gtmScript) {
        gtmScript.remove();
      }

      // Remove the noscript iframe
      const gtmNoscript = document.querySelector(`noscript iframe[src*="googletagmanager.com/ns.html?id=${gtmId}"]`);
      if (gtmNoscript?.parentElement) {
        gtmNoscript.parentElement.remove();
      }
    };
  }, [gtmId]);
}

/**
 * Push an event to the GTM dataLayer
 *
 * @param event - The event object to push to dataLayer
 *
 * @example
 * ```tsx
 * import { pushToDataLayer } from 'shared/client/hooks/useGoogleTagManager';
 *
 * // Track a custom event
 * pushToDataLayer({
 *   event: 'button_click',
 *   button_name: 'Submit'
 * });
 * ```
 */
export function pushToDataLayer(event: Record<string, unknown>) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(event);
}
